<template>
  <div class="container" v-if="!isLoading">
    <div class="container-header">
      <div class="user-menu" ref="menuRef">
        <button type="button" class="user-button" @click="toggleMenu">
          <div class="user-info">
            <img 
              v-if="currentStudentPhoto" 
              :src="currentStudentPhoto" 
              class="student-avatar"
              alt="Student photo"
            />
            <div v-else class="student-avatar-placeholder">
              {{ currentStudentName.charAt(0) }}
            </div>
           
          </div>
          <span class="arrow" :class="{ 'arrow-up': isMenuOpen }">
            <IconArrow />
          </span>
        </button>
        
        <div v-if="isMenuOpen" class="dropdown-menu">
          <div v-if="hasMultipleChildren" class="children-list">
            <div 
              v-for="child in children" 
              :key="child.id"
              class="menu-item child-item"
              :class="{ active: child.id === selectedChildId }"
              @click="selectChild(child)"
            >
              <img 
                v-if="childPhotos[child.id]" 
                :src="childPhotos[child.id]" 
                class="child-avatar"
                alt="Student photo"
              />
              <div v-else class="child-avatar-placeholder">
                {{ child.first_name?.charAt(0) }}
              </div>
              <span>{{ child.first_name }}</span>
            </div>
            <div class="menu-divider"></div>
          </div>
          
          <div class="menu-item" @click="goToStudentPage">
            Personal Details
          </div>
          
          <div class="menu-item logout" @click="handleLogout">
            Log out
          </div>
        </div>
      </div>
    </div>
    
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch, computed, ref, onUnmounted } from 'vue';
import { useParentAuthStore } from '@/store/auth';
import { useKeycloak } from '@/composables/useKeycloak';
import { logout as keycloakLogout } from '@/composables/useKeycloak';
import { useRouter } from 'vue-router';
import { useParentStudents } from './composables/useParentStudents';
import { getStudentPhoto, createPhotoUrl, revokePhotoUrl } from '@/composables/useStudentPhoto';
import IconArrow from '@/assets/icon-arrow.svg'

const authStore = useParentAuthStore();
const { token } = useKeycloak();
const router = useRouter();
const { data: childrenData } = useParentStudents();

const childPhotos = ref<Record<string, string>>({});
const photoUrls = ref<string[]>([]);
const selectedChild = ref<any>(null);

async function loadChildPhotos(children: any[]) {
  if (!children || children.length === 0) return;
  
  for (const child of children) {
    try {
      const photoBlob = await getStudentPhoto(child.id);
      if (photoBlob) {
        const url = createPhotoUrl(photoBlob);
        childPhotos.value[child.id] = url;
        photoUrls.value.push(url);
      }
    } catch (error) {
      console.error(`Failed to load photo for child ${child.id}:`, error);
    }
  }
}

onUnmounted(() => {
  photoUrls.value.forEach(url => revokePhotoUrl(url));
});

watch(() => childrenData.value, async (children) => {
  authStore.setParentChildren(children);
  if (children && children.length > 0) {
    await loadChildPhotos(children);

    const savedChildId = localStorage.getItem('selected_child_id');
    const savedChild = children.find((c: any) => c.id === savedChildId);
    
    if (savedChild) {
      selectedChild.value = savedChild;
      authStore.setParentChild(savedChild);
    } else {
      selectedChild.value = children[0];
      authStore.setParentChild(children[0]);
    }
  }
}) 

const isMenuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

const isLoading = computed(() => !authStore.state.isInitialized);
const children = computed(() => authStore.state.parentChildren || []);

const selectedChildId = computed(() => {
  return selectedChild.value?.id;
});

const currentStudentName = computed(() => {
  return selectedChild.value?.first_name || 'Student';
});

const currentStudentPhoto = computed(() => {
  return selectedChild.value ? childPhotos.value[selectedChild.value.id] : null;
});

const hasMultipleChildren = computed(() => children.value.length > 1);

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
}

async function selectChild(child: any) {
  console.log('Selected child:', child);
  localStorage.setItem('selected_child_id', child.id);
  authStore.setParentChild(child);
  selectedChild.value = child; 
  isMenuOpen.value = false;
}


function handleClickOutside(event: MouseEvent) {
  if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
    isMenuOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(token, async (newToken) => {
  if (newToken) {
    console.log('watch');
    authStore.setKeycloakToken(newToken);
  }
}, { immediate: true });

function handleLogout() {
  authStore.clear();
  keycloakLogout();
}

function goToStudentPage() {
  router.push('/parent/students');
  isMenuOpen.value = false;
}
</script>

<style lang="scss" scoped>
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  width: 100%;
  height: 100%;
}

body {
  margin: 0;
  padding: 0;
  font-weight: 400;
  width: 100%;
  height: 100%;
}

.container-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  padding: 10px 20px;
}

.user-menu {
  position: relative;
}

.user-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;


  .student-name {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .arrow {
    font-size: 10px;
    transition: transform 0.2s;
    
    &.arrow-up {
      transform: rotate(180deg);
    }
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
}

.menu-item {
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;

  &:hover {
    background: #f5f5f5;
  }

  &.active {
    background: #FFF8D3;
  }

  &.logout {
    color: #d32f2f;
    
    &:hover {
      background: #ffebee;
    }
  }
}

.children-list {
  .menu-item {
    padding-left: 24px;
  }
}

.menu-divider {
  height: 1px;
  background: #ddd;
  margin: 4px 0;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.student-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.student-avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1976d2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 16px;
  text-transform: uppercase;
}

.child-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
}

.child-avatar-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  margin-right: 8px;
  text-transform: uppercase;
}

.child-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px; 
  
  .student-name {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.menu-item {
  display: flex;
  align-items: center;
}
</style>